import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  ArchiveBoxIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  ArrowsRightLeftIcon,
  BellIcon,
  ChatBubbleLeftIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  NoSymbolIcon,
  EyeIcon,
  StarIcon as StarOutline,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolid } from '@heroicons/react/24/solid'
import type { Application, Currency, RejectReason, SalaryPeriod, SalaryRange, SubscriptionPlan, UserSettings } from '../types'
import { rejectOptions, statusBadgeClass } from '../utils'
import { convertSalaryRange, convertCurrency, convertPeriod, CURRENCY_SYMBOLS, convertToDisplayFormat, getFormatSuffix, formatCurrencyAmount } from '../utils/currency'
import { STATUS_LABELS, isTerminalStatus } from '../utils/statusMachine'
import { formatDate, formatRelativeDate } from '../utils/dateFormat'
import { CompactApplicationCard } from './CompactApplicationCard'
import { ApplicationsToolbar } from './ApplicationsToolbar'
import { GridLoadingSkeleton, TableLoadingSkeleton } from './ApplicationsSkeletons'
import { ApplicationsEmptyState } from './ApplicationsEmptyState'
import { ExtensionBanner } from './ExtensionBanner'
import { StagesProgress } from './StagesProgress'
import { getRowHighlightClass, truncateText } from './ApplicationsTableHelpers'
import { NoteModal } from './modals/NoteModal'
import { StatusChangeModal } from './modals/StatusChangeModal'
import { RejectModal } from './modals/RejectModal'
import { ArchiveConfirmationModal } from './modals/ArchiveConfirmationModal'

type SortKey = 'company' | 'status' | 'salary' | 'stages' | 'createdAt' | 'updatedAt'
type SortDir = 'asc' | 'desc'

type Props = {
  applications: Application[]
  isLoading: boolean
  onToggleStage: (appId: string, stageId: string) => void
  onUpdateStages: (appId: string, stages: Application['processStages']) => void
  onUpdateCompensation: (
    appId: string,
    payload: {
      salaryRange: SalaryRange
      targetAmount: number | null
      targetPeriod: SalaryPeriod
      targetCurrency: Currency
    },
  ) => Promise<void> | void
  onUpdateStatus: (appId: string, status: Application['status'], offerReason?: string | null) => void
  onStartReject: (appId: string) => void
  onSubmitReject: (appId: string, reason: RejectReason) => void
  onCancelReject: () => void
  onArchive: (appId: string) => void
  onUnarchive: (appId: string) => void
  onUpdateNote: (appId: string, note: string) => void
  onSetFollowUp?: (appId: string) => void
  openRejectId: string | null
  rejectState: RejectReason
  setRejectState: (reason: RejectReason) => void
  viewMode: 'active' | 'archived'
  displayMode: 'cards' | 'table'
  page: number
  totalPages: number
  pageSize: number
  totalItems: number
  onPageChange: (page: number) => void
  settings: UserSettings
  onToggleFavorite: (appId: string) => void
  onSettingsChange: (settings: UserSettings) => void
  plan: SubscriptionPlan
  sortKey: SortKey
  sortDir: SortDir
}

export const ApplicationsList = ({
  applications,
  isLoading,
  onToggleStage: _onToggleStage,
  onUpdateStages: _onUpdateStages,
  onUpdateCompensation: _onUpdateCompensation,
  onUpdateStatus,
  onStartReject,
  onSubmitReject,
  onCancelReject,
  onArchive,
  onUnarchive,
  onUpdateNote,
  onSetFollowUp,
  openRejectId,
  rejectState,
  setRejectState,
  viewMode,
  displayMode,
  page,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  settings,
  onToggleFavorite,
  onSettingsChange,
  plan,
  sortKey: initialSortKey,
  sortDir: initialSortDir,
}: Props) => {
  const navigate = useNavigate()
  const [openQuickMenuId, setOpenQuickMenuId] = useState<string | null>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const quickMenuRef = useRef<HTMLDivElement>(null)
  const triggerBtnRef = useRef<HTMLButtonElement | null>(null)

  const updateMenuPos = useCallback((btn: HTMLButtonElement) => {
    const rect = btn.getBoundingClientRect()
    setMenuPos({ top: rect.bottom + 4, left: rect.right - 208 })
  }, [])

  const openQuickMenu = useCallback((appId: string, buttonEl: HTMLButtonElement) => {
    if (openQuickMenuId === appId) {
      setOpenQuickMenuId(null)
      setMenuPos(null)
      triggerBtnRef.current = null
      return
    }
    triggerBtnRef.current = buttonEl
    updateMenuPos(buttonEl)
    setOpenQuickMenuId(appId)
  }, [openQuickMenuId, updateMenuPos])

  // Helper to check if column is visible
  const isColumnVisible = (col: string) => settings.visibleColumns.includes(col as any)

  // Note modal state
  const [isQuickNoteOpen, setIsQuickNoteOpen] = useState(false)
  const [quickNoteAppId, setQuickNoteAppId] = useState<string | null>(null)
  const [quickNoteDraft, setQuickNoteDraft] = useState('')

  // Status modal state
  const [statusModalAppId, setStatusModalAppId] = useState<string | null>(null)
  const [statusDraft, setStatusDraft] = useState<Application['status']>('applied')
  const [offerReasonDraft, setOfferReasonDraft] = useState('')

  // Archive confirmation modal state
  const [archiveModalAppId, setArchiveModalAppId] = useState<string | null>(null)

  // Use sort state from props
  const sortKey = initialSortKey
  const sortDir = initialSortDir

  // Bulk selection state
  const [bulkEditMode, setBulkEditMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Sort applications
  const sortedApplications = useMemo(() => {
    const arr = [...applications]
    arr.sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'company':
          cmp = a.company.localeCompare(b.company)
          break
        case 'status':
          cmp = a.status.localeCompare(b.status)
          break
        case 'salary':
          cmp = (a.salaryRange?.min ?? 0) - (b.salaryRange?.min ?? 0)
          break
        case 'stages': {
          const aProgress = a.processStages.length
            ? a.processStages.filter((s) => s.completed).length / a.processStages.length
            : 0
          const bProgress = b.processStages.length
            ? b.processStages.filter((s) => s.completed).length / b.processStages.length
            : 0
          cmp = aProgress - bProgress
          break
        }
        case 'createdAt':
          cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
        case 'updatedAt':
          cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
          break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [applications, sortKey, sortDir])

  // Bulk selection handlers
  const allSelected = applications.length > 0 && selectedIds.size === applications.length
  const someSelected = selectedIds.size > 0 && selectedIds.size < applications.length

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(applications.map((a) => a.id)))
    }
  }

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Bulk actions
  const handleBulkArchive = () => {
    selectedIds.forEach((id) => onArchive(id))
    setSelectedIds(new Set())
  }

  const handleBulkUnarchive = () => {
    selectedIds.forEach((id) => onUnarchive(id))
    setSelectedIds(new Set())
  }

  // Open status modal
  const openStatusModal = (app: Application) => {
    setStatusModalAppId(app.id)
    setStatusDraft(app.status)
    setOfferReasonDraft(app.offerDecisionReason ?? '')
  }

  // Close status modal
  const closeStatusModal = () => {
    setStatusModalAppId(null)
    setStatusDraft('applied')
    setOfferReasonDraft('')
  }

  // Handle status change
  const handleStatusChange = () => {
    if (statusModalAppId) {
      onUpdateStatus(statusModalAppId, statusDraft, offerReasonDraft || null)
      closeStatusModal()
    }
  }

  useEffect(() => {
    if (!openQuickMenuId) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenQuickMenuId(null)
        setMenuPos(null)
      }
    }

    const onPointerDown = (e: PointerEvent) => {
      const el = e.target as Element | null
      if (
        !el?.closest?.('[data-quick-actions="true"]') &&
        !quickMenuRef.current?.contains(el as Node)
      ) {
        setOpenQuickMenuId(null)
        setMenuPos(null)
      }
    }

    const onScroll = () => {
      if (triggerBtnRef.current) updateMenuPos(triggerBtnRef.current)
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [openQuickMenuId])

  // Clear selection and exit bulk mode when page/view changes
  useEffect(() => {
    setSelectedIds(new Set())
    setBulkEditMode(false)
  }, [page, viewMode])


  const slaInfo = (app: Application) => {
    if (app.status !== 'applied' && app.status !== 'in_process') return null
    const base = new Date(app.updatedAt || app.createdAt).getTime()
    const days = Math.floor((Date.now() - base) / (1000 * 60 * 60 * 24))
    if (!Number.isFinite(days)) return null
    if (days >= 14) return { level: 'critical' as const, days }
    if (days >= 7) return { level: 'warn' as const, days }
    return null
  }

  const statusModalApp = applications.find((a) => a.id === statusModalAppId) ?? null
  const rejectModalApp = applications.find((a) => a.id === openRejectId) ?? null
  const archiveModalApp = applications.find((a) => a.id === archiveModalAppId) ?? null

  return (
    <div className="w-full card p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-lg font-semibold font-display text-app-ink">Your applications</p>
          <p className="text-sm text-app-muted">
            {viewMode === 'archived'
              ? 'Archive view. Restore an item to bring it back to active.'
              : 'Use action buttons to change status, reject, or archive.'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-app-muted">
          <span className="text-xs text-app-muted">
            {totalItems === 0
              ? 'No data'
              : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalItems)} / ${totalItems}`}
          </span>
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="rounded-lg border border-app-line px-2 py-1 text-xs font-semibold text-app-ink hover:bg-app-subtle disabled:opacity-50"
          >
            ←
          </button>
          <span className="text-xs text-app-muted">
            {page}/{totalPages}
          </span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="rounded-lg border border-app-line px-2 py-1 text-xs font-semibold text-app-ink hover:bg-app-subtle disabled:opacity-50"
          >
            →
          </button>
        </div>
      </div>

      {/* Extension banner for new users */}
      <ExtensionBanner />

      {/* Toolbar */}
      <ApplicationsToolbar
        settings={settings}
        onSettingsChange={onSettingsChange}
        plan={plan}
        displayMode={displayMode}
        hasApplications={applications.length > 0}
        bulkEditMode={bulkEditMode}
        onToggleBulkEdit={() => setBulkEditMode(true)}
        onNavigateToPricing={() => navigate('/pricing')}
      />

      {/* Bulk actions toolbar */}
      {bulkEditMode && (
        <div className="flex items-center gap-3 rounded-lg border border-sky-300/50 dark:border-app-line bg-sky-100/30 dark:bg-app-highlight px-3 py-2">
          <span className="text-sm text-sky-700 dark:text-app-accent">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select items'}
          </span>
          {selectedIds.size > 0 && (
            <>
              <div className="h-4 w-px bg-app-line" />
              {viewMode === 'active' ? (
                <button
                  onClick={handleBulkArchive}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-app-ink hover:bg-app-subtle transition-colors rounded-lg"
                >
                  <ArchiveBoxIcon className="h-3.5 w-3.5" />
                  Archive selected
                </button>
              ) : (
                <button
                  onClick={handleBulkUnarchive}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-200 hover:bg-emerald-100/30 dark:hover:bg-emerald-900/30 transition-colors rounded-lg"
                >
                  <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                  Restore selected
                </button>
              )}
            </>
          )}
          <button
            onClick={() => {
              setSelectedIds(new Set())
              setBulkEditMode(false)
            }}
            className="ml-auto text-xs text-app-muted hover:text-app-ink"
          >
            Done
          </button>
        </div>
      )}

      <div className="space-y-3">
      {isLoading ? (
        displayMode === 'table' ? <TableLoadingSkeleton /> : <GridLoadingSkeleton />
      ) : applications.length === 0 ? (
        <ApplicationsEmptyState viewMode={viewMode} />
      ) : displayMode === 'table' ? (
        <>
        <div className="overflow-x-auto rounded-lg border border-app-line">
          <table className="min-w-full divide-y divide-app-line text-sm text-app-ink">
            <thead className="bg-app-page/70 text-xs uppercase text-app-muted sticky top-0 z-10">
              <tr>
                {/* Checkbox column - only visible in bulk edit mode */}
                {bulkEditMode && (
                  <th className="px-3 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected
                      }}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 border-app-line bg-app-subtle text-app-muted focus:ring-app-line focus:ring-offset-white dark:focus:ring-offset-app-line rounded-lg"
                    />
                  </th>
                )}
                {isColumnVisible('favorite') && (
                  <th className="px-2 py-2 w-10 text-center" title="Favorites">
                    <StarOutline className="h-4 w-4 mx-auto text-app-muted" />
                  </th>
                )}
                {isColumnVisible('company') && (
                  <th className="px-3 py-2 text-left">Company</th>
                )}
                {isColumnVisible('status') && (
                  <th className="px-3 py-2 text-left w-28">Status</th>
                )}
                {isColumnVisible('salary') && (
                  <th className="px-3 py-2 text-left hidden lg:table-cell">Salary</th>
                )}
                {isColumnVisible('target') && (
                  <th className="px-3 py-2 text-left hidden xl:table-cell">Target</th>
                )}
                {isColumnVisible('stages') && (
                  <th className="px-3 py-2 text-left hidden md:table-cell">Progress</th>
                )}
                {/* Note indicator column */}
                {isColumnVisible('note') && (
                  <th className="px-2 py-2 w-10 text-center" title="Notes">
                    <ChatBubbleLeftIcon className="h-4 w-4 mx-auto" />
                  </th>
                )}
                {isColumnVisible('createdAt') && (
                  <th className="px-3 py-2 text-left hidden sm:table-cell">Added</th>
                )}
                {isColumnVisible('updatedAt') && (
                  <th className="px-3 py-2 text-left hidden xl:table-cell">Updated</th>
                )}
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-app-line bg-app-surface">
              {sortedApplications.map((app) => {
                const completed = app.processStages.filter((s) => s.completed).length
                const total = app.processStages.length
                
                // Apply currency conversion if enabled
                const displayRange = settings.currencyConversionEnabled && app.salaryRange
                  ? convertSalaryRange(app.salaryRange, settings.targetCurrency)
                  : app.salaryRange
                
                // Format salary with display format and period
                const formatSalaryWithSettings = () => {
                  if (!displayRange) return '—'
                  const currency = (displayRange.currency as Currency) || 'USD'
                  const symbol = CURRENCY_SYMBOLS[currency] || currency
                  // Use original period when format is 'original', otherwise use display format
                  const suffix = settings.salaryDisplayFormat === 'original'
                    ? (displayRange.period === 'hourly' ? '/h' : displayRange.period === 'yearly' ? '/yr' : '/mo')
                    : getFormatSuffix(settings.salaryDisplayFormat)
                  // First convert to monthly base, then to target format
                  const min = settings.salaryDisplayFormat === 'original'
                    ? displayRange.min
                    : convertToDisplayFormat(
                        convertPeriod(displayRange.min, displayRange.period, 'monthly'),
                        settings.salaryDisplayFormat
                      )
                  const max = settings.salaryDisplayFormat === 'original'
                    ? displayRange.max
                    : convertToDisplayFormat(
                        convertPeriod(displayRange.max, displayRange.period, 'monthly'),
                        settings.salaryDisplayFormat
                      )
                  if (min == null || max == null) return '—'
                  return `${min.toLocaleString('pl-PL')} – ${max.toLocaleString('pl-PL')} ${symbol}${suffix}`
                }
                const salary = formatSalaryWithSettings()
                
                // Format target with new target fields
                const formatTargetWithSettings = () => {
                  if (app.targetAmount == null) return '—'
                  // Get original currency from app, default to USD if 'original' or missing
                  const appCurrency = (app.targetCurrency && app.targetCurrency !== 'original') ? app.targetCurrency : 'USD'
                  const appPeriod = app.targetPeriod || 'monthly'
                  
                  // Apply currency conversion if enabled and targetCurrency is not 'original'
                  let displayAmount = app.targetAmount
                  let displayCurrency = appCurrency
                  if (settings.currencyConversionEnabled && settings.targetCurrency !== 'original') {
                    // If currencies are the same, no conversion needed
                    if (appCurrency === settings.targetCurrency) {
                      displayCurrency = settings.targetCurrency
                    } else {
                      // Convert to target currency
                      const converted = convertCurrency(app.targetAmount, appCurrency, settings.targetCurrency as Exclude<Currency, 'original'>)
                      if (converted != null) {
                        displayAmount = converted
                        displayCurrency = settings.targetCurrency
                      }
                    }
                  }
                  // If targetCurrency is 'original', keep original currency (already set above)
                  
                  // Convert period if format changed
                  const suffix = settings.salaryDisplayFormat === 'original'
                    ? (appPeriod === 'hourly' ? '/h' : appPeriod === 'yearly' ? '/yr' : '/mo')
                    : getFormatSuffix(settings.salaryDisplayFormat)
                  
                  let finalAmount = displayAmount
                  if (settings.salaryDisplayFormat !== 'original') {
                    // First convert to monthly base, then to target format
                    const monthlyAmount = convertPeriod(displayAmount, appPeriod, 'monthly')
                    finalAmount = convertToDisplayFormat(monthlyAmount, settings.salaryDisplayFormat) || displayAmount
                  }
                  
                  const formatted = formatCurrencyAmount(finalAmount, displayCurrency)
                  return `${formatted}${suffix}`
                }
                const expected = formatTargetWithSettings()
                const sla = slaInfo(app)
                const isSelected = selectedIds.has(app.id)
                const rowHighlight = getRowHighlightClass(app.status)

                return (
                  <tr
                    key={app.id}
                    onClick={() => navigate(`/app/${app.id}`)}
                    className={`hover:bg-app-subtle transition-colors cursor-pointer ${rowHighlight} ${
                      isSelected ? 'bg-sky-50 dark:bg-app-highlight' : ''
                    }`}
                  >
                    {/* Checkbox - only visible in bulk edit mode */}
                    {bulkEditMode && (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(app.id)}
                          className="h-4 w-4 border-app-line bg-app-surface text-app-muted focus:ring-app-line focus:ring-offset-app-line rounded-lg"
                        />
                      </td>
                    )}
                    {/* Favorite */}
                    {isColumnVisible('favorite') && (
                      <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => onToggleFavorite(app.id)}
                          className={`p-0.5 rounded transition-colors ${
                            app.favorite
                              ? 'text-amber-700 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300'
                              : 'text-app-muted hover:text-amber-700'
                          }`}
                          title={app.favorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          {app.favorite ? (
                            <StarSolid className="h-4 w-4" />
                          ) : (
                            <StarOutline className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                    )}
                    {isColumnVisible('company') && (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-semibold text-app-ink truncate">{app.company}</span>
                          <a
                            href={app.sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-app-muted hover:text-app-accent-hover transition-colors shrink-0"
                            title="Open job posting"
                          >
                            <LinkIcon className="h-3.5 w-3.5" />
                          </a>
                          {sla && (
                            <span
                              className={`shrink-0 ${
                                sla.level === 'critical'
                                  ? 'text-rose-700 dark:text-rose-400'
                                  : 'text-amber-700 dark:text-amber-400'
                              }`}
                              title={`No response: ${sla.days} days`}
                            >
                              <ExclamationTriangleIcon className="h-4 w-4" />
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    {/* Status badge */}
                    {isColumnVisible('status') && (
                      <td className="px-3 py-2 w-28">
                        <span
                          className={`badge ${statusBadgeClass[app.status]}`}
                          title={
                            app.status === 'rejected' && app.rejectReason
                              ? `${rejectOptions.find((r) => r.code === app.rejectReason?.code)?.label ?? 'Other'}${app.rejectReason.details ? `: ${app.rejectReason.details}` : ''}`
                              : undefined
                          }
                        >
                          {STATUS_LABELS[app.status]}
                        </span>
                        {(app.status === 'offer_declined' || app.status === 'accepted') && app.offerDecisionReason && (
                          <p className="text-[11px] text-app-muted mt-1 truncate max-w-[100px]" title={app.offerDecisionReason}>
                            {app.offerDecisionReason}
                          </p>
                        )}
                      </td>
                    )}
                    {isColumnVisible('salary') && (
                      <td className="px-3 py-2 text-app-ink hidden lg:table-cell min-w-[140px]">{salary}</td>
                    )}
                    {isColumnVisible('target') && (
                      <td className="px-3 py-2 text-app-ink hidden xl:table-cell min-w-[110px]">{expected}</td>
                    )}
                    {isColumnVisible('stages') && (
                      <td className="px-3 py-2 hidden md:table-cell">
                        <StagesProgress completed={completed} total={total} />
                      </td>
                    )}
                    {/* Note indicator */}
                    {isColumnVisible('note') && (
                      <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        {app.note ? (
                          <div className="group relative">
                            <button
                              onClick={() => {
                                setQuickNoteAppId(app.id)
                                setQuickNoteDraft(app.note ?? '')
                                setIsQuickNoteOpen(true)
                              }}
                              className="inline-flex items-center justify-center rounded-full p-1.5 text-app-accent hover:bg-app-surface transition-colors"
                              aria-label="View/edit note"
                            >
                              <ChatBubbleLeftIcon className="h-4 w-4 fill-sky-400/20" />
                            </button>
                            {/* Tooltip with note preview */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-app-surface border border-app-line rounded-lg shadow-xl text-xs text-app-ink w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-30 pointer-events-none">
                              <p className="whitespace-pre-wrap break-words">{truncateText(app.note, 150)}</p>
                              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-app-line" />
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setQuickNoteAppId(app.id)
                              setQuickNoteDraft('')
                              setIsQuickNoteOpen(true)
                            }}
                            className="inline-flex items-center justify-center rounded-full p-1.5 text-app-muted hover:text-app-ink hover:bg-app-subtle transition-colors"
                            title="Add note"
                            aria-label="Add note"
                          >
                            <ChatBubbleLeftIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    )}
                    {isColumnVisible('createdAt') && (
                      <td
                        className="px-3 py-2 text-app-ink hidden sm:table-cell text-xs"
                        title={formatDate(app.createdAt, settings.dateFormat)}
                      >
                        {formatRelativeDate(app.createdAt)}
                      </td>
                    )}
                    {isColumnVisible('updatedAt') && (
                      <td
                        className="px-3 py-2 text-app-muted hidden md:table-cell text-xs min-w-[90px]"
                        title={formatDate(app.updatedAt, settings.dateFormat)}
                      >
                        {formatRelativeDate(app.updatedAt)}
                      </td>
                    )}
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {/* Change status button */}
                        <button
                          onClick={() => openStatusModal(app)}
                          disabled={isTerminalStatus(app.status)}
                          className={`rounded-lg p-1.5 transition-colors ${
                            isTerminalStatus(app.status)
                              ? 'border border-app-line text-app-muted cursor-not-allowed'
                              : 'border border-app-line text-app-ink hover:bg-app-subtle'
                          }`}
                          title={isTerminalStatus(app.status) ? 'Final status - no changes allowed' : 'Change status'}
                          aria-label={isTerminalStatus(app.status) ? 'Final status' : 'Change status'}
                        >
                          <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
                        </button>

                        {/* Restore button for archived items */}
                        {app.archived && (
                          <button
                            onClick={() => onUnarchive(app.id)}
                            className="rounded-lg border border-emerald-200 dark:border-emerald-800/50 p-1.5 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 hover:border-emerald-700/50 transition-colors"
                            title="Restore to active"
                            aria-label="Restore to active"
                          >
                            <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}

                        {/* More actions menu */}
                        <div className="relative" data-quick-actions="true">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              openQuickMenu(app.id, e.currentTarget)
                            }}
                            className="rounded-lg border border-app-line p-1.5 text-app-ink hover:bg-app-subtle transition-colors"
                            title="More actions"
                            aria-label="More actions"
                          >
                            <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {/* Quick action menu rendered via portal to escape overflow clipping */}
        {openQuickMenuId && menuPos && (() => {
          const app = sortedApplications.find((a) => a.id === openQuickMenuId)
          if (!app) return null
          return createPortal(
            <div
              ref={quickMenuRef}
              style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
              className="w-52 rounded-lg border border-app-line bg-app-surface p-1.5 shadow-xl z-[9999]"
            >
              <button
                onClick={() => {
                  setOpenQuickMenuId(null)
                  setMenuPos(null)
                  navigate(`/app/${app.id}`)
                }}
                className="w-full text-left px-3 py-2 text-sm font-medium text-app-accent hover:bg-app-highlight inline-flex items-center gap-2 rounded-lg"
              >
                <EyeIcon className="h-4 w-4" aria-hidden="true" />
                View details
              </button>
              <div className="my-1 h-px bg-app-subtle" />
              {app.archived ? (
                <button
                  onClick={() => {
                    setOpenQuickMenuId(null)
                    setMenuPos(null)
                    onUnarchive(app.id)
                  }}
                  className="w-full text-left px-3 py-2 text-sm font-medium text-emerald-600 dark:text-emerald-100 hover:bg-emerald-50 dark:hover:bg-emerald-900/40 inline-flex items-center gap-2 rounded-lg"
                >
                  <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
                  Restore to active
                </button>
              ) : (
                <button
                  onClick={() => {
                    setOpenQuickMenuId(null)
                    setMenuPos(null)
                    setArchiveModalAppId(app.id)
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-app-ink hover:bg-app-subtle inline-flex items-center gap-2 rounded-lg"
                >
                  <ArchiveBoxIcon className="h-4 w-4" aria-hidden="true" />
                  Archive
                </button>
              )}
              <button
                onClick={() => {
                  setOpenQuickMenuId(null)
                  setMenuPos(null)
                  onSetFollowUp?.(app.id)
                }}
                className="w-full text-left px-3 py-2 text-sm text-app-ink hover:bg-app-subtle inline-flex items-center gap-2 rounded-lg"
              >
                <BellIcon className="h-4 w-4" aria-hidden="true" />
                {app.followUpAt ? 'Edit reminder' : 'Set reminder'}
              </button>
              {!app.archived && app.status !== 'rejected' && !isTerminalStatus(app.status) && (
                <button
                  onClick={() => {
                    setOpenQuickMenuId(null)
                    setMenuPos(null)
                    setRejectState({ code: 'too_expensive' })
                    onStartReject(app.id)
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 inline-flex items-center gap-2 rounded-lg"
                >
                  <NoSymbolIcon className="h-4 w-4" aria-hidden="true" />
                  Reject
                </button>
              )}
              <div className="my-1 h-px bg-app-subtle" />
              <a
                href={app.sourceUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  setOpenQuickMenuId(null)
                  setMenuPos(null)
                }}
                className={`w-full inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
                  app.sourceUrl
                    ? 'text-app-ink hover:bg-app-subtle'
                    : 'text-app-muted pointer-events-none'
                }`}
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                Open job link
              </a>
            </div>,
            document.body
          )
        })()}
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {sortedApplications.map((app) => (
            <CompactApplicationCard
              key={app.id}
              app={app}
              settings={settings}
              onToggleFavorite={onToggleFavorite}
              onOpenNoteModal={(appId) => {
                setQuickNoteAppId(appId)
                setQuickNoteDraft(sortedApplications.find((a) => a.id === appId)?.note ?? '')
                setIsQuickNoteOpen(true)
              }}
              onUpdateStatus={(appId, newStatus) => {
                onUpdateStatus(appId, newStatus, null)
              }}
              onSubmitReject={onSubmitReject}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              onToggleStage={_onToggleStage}
              onSetFollowUp={onSetFollowUp}
              bulkEditMode={bulkEditMode}
              isSelected={selectedIds.has(app.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}
      </div>

      {/* Note Modal */}
      <NoteModal
        isOpen={isQuickNoteOpen}
        appId={quickNoteAppId}
        noteDraft={quickNoteDraft}
        onNoteDraftChange={setQuickNoteDraft}
        onClose={() => {
          setIsQuickNoteOpen(false)
          setQuickNoteAppId(null)
        }}
        onSave={onUpdateNote}
      />

      {/* Status Change Modal */}
      <StatusChangeModal
        app={statusModalApp}
        statusDraft={statusDraft}
        offerReasonDraft={offerReasonDraft}
        onStatusDraftChange={setStatusDraft}
        onOfferReasonDraftChange={setOfferReasonDraft}
        onClose={closeStatusModal}
        onSave={handleStatusChange}
      />

      {/* Reject Modal */}
      <RejectModal
        app={rejectModalApp}
        rejectState={rejectState}
        onRejectStateChange={setRejectState}
        onCancel={onCancelReject}
        onSubmit={onSubmitReject}
      />

      {/* Archive Confirmation Modal */}
      <ArchiveConfirmationModal
        app={archiveModalApp}
        onClose={() => setArchiveModalAppId(null)}
        onConfirm={onArchive}
      />
    </div>
  )
}

